import type { FlightLeg } from '../../../../domain/types'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import {
  buildAirlineServiceLabel,
  formatAirlineServicesTitle,
  getDistinctAirlineNames,
  hasFlightAirlineServicesContent,
  hasUrl,
} from '../../../utils/flightAirlineServices'
import styles from './FlightAirlineServices.module.css'
import { QuestionRow } from './QuestionRow'

export interface FlightAirlineServicesProps {
  legs: FlightLeg[]
}

export function FlightAirlineServices({ legs }: FlightAirlineServicesProps) {
  const { formatMessage } = useIntl()
  const airlineBaggageFaqUrl = useConfiguration('links.airlineBaggageFaq')
  const airlineSeatReservationFaqUrl = useConfiguration('links.airlineSeatReservationFaq')
  const airlineOnlineCheckInFaqUrl = useConfiguration('links.airlineOnlineCheckInFaq')
  const checkInLegs = legs.filter(leg => hasUrl(leg.airlineCheckInURL))
  const seatReservationLegs = legs.filter(leg => hasUrl(leg.airlineSeatReservationURL))
  const hasBaggageFaq = hasUrl(airlineBaggageFaqUrl ?? '')
  const distinctAirlineNames = getDistinctAirlineNames(legs)
  const baseTitle = formatMessage({ id: 'booking_details_flight_airline_services_title' })
  const formattedAirlineServicesTitle = formatAirlineServicesTitle(baseTitle, distinctAirlineNames)
  if (!hasFlightAirlineServicesContent(legs, airlineBaggageFaqUrl)) {
    return null
  }

  return (
    <div className={styles.section}>
      <Typography as="h4" variant="body-m-bold-tight">
        {formattedAirlineServicesTitle}
      </Typography>
      <ul className={styles.items}>
        {hasBaggageFaq && (
          <li key="baggage-conditions" className={styles.item}>
            <QuestionRow
              label={formatMessage({ id: 'booking_details_flight_airline_services_baggage_conditions' })}
              href={airlineBaggageFaqUrl!}
              icon="ChevronRight"
            />
          </li>
        )}
        {seatReservationLegs.map(leg => (
          <li key={`seat-reservation-${leg.flightNumber}`} className={styles.item}>
            <QuestionRow
              label={buildAirlineServiceLabel(formatMessage({ id: 'booking_details_flight_airline_services_reserve_seat' }), leg, seatReservationLegs.length > 1)}
              href={airlineSeatReservationFaqUrl ?? leg.airlineSeatReservationURL}
              icon="SquareArrowUpRight"
            />
          </li>
        ))}
        {checkInLegs.map(leg => (
          <li key={`check-in-${leg.flightNumber}`} className={styles.item}>
            <QuestionRow
              label={buildAirlineServiceLabel(formatMessage({ id: 'booking_details_flight_airline_services_online_check_in' }), leg, checkInLegs.length > 1)}
              href={airlineOnlineCheckInFaqUrl ?? leg.airlineCheckInURL}
              icon="SquareArrowUpRight"
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
